import { isElectron, pictureInPicture, room } from "../../../libs/roomnew";
import { getPerformanceState } from "../performance-optimizer/functions";
import { _getSourceNames } from "../tracks/functions";
import { QUALITY_EVENTS, RESOLUTION } from "./constants"
import { getAppState } from "./electron";
import { getCurrentMaxResolution, getForwardedEndpoints, getForwardedResolutions, getResolutionPerScreen, getScreenVisibleParticipants, hasForwardedEndpointsChanged, hasForwardedResolutionChanged, setCurrentMaxResolution, setForwardedEndpoints, setForwardedResolutions } from "./functions";

export const videoQualityEventListeners=()=>{
    window.addEventListener(QUALITY_EVENTS.VISIBLE_ENDPOINTS_CHANGED,({detail:{source,oldEndpoints,newEndpoints}})=>{
        console.log(`%c[QUALITY_EVENTS]%c - VISIBLE_ENDPOINTS_CHANGED`,'color:green','color:unset','source',source,'oldEndpoints',oldEndpoints,'newEndpoints',newEndpoints)
        updateVideoQuality();
    })
    window.addEventListener(QUALITY_EVENTS.APP_STATE_CHANGED,({detail:{state}})=>{
        console.log(`%c[QUALITY_EVENTS]%c - APP_STATE_CHANGED`,'color:green','color:unset','State:',state)
        updateVideoQuality(false,true);
    })
}

export const updateVideoQuality=(resolution=false,sourcesChanged=false)=>{
    console.log("[RESOLUTION_UPDATE] updateVideoQuality, app state", getAppState())
    if(!room){return}
    const visibleEndpoints=getScreenVisibleParticipants();
    const resolutions=getResolutionPerScreen(visibleEndpoints);
    const forwardedEndpoints=getForwardedEndpoints();
    const forwardedResolutions=getForwardedResolutions();
    if(hasForwardedEndpointsChanged(forwardedEndpoints,visibleEndpoints) || hasForwardedResolutionChanged(forwardedResolutions,resolutions) || sourcesChanged){
        debouncedSendReceiverVideoConstraints(visibleEndpoints,resolutions);
    }else{
        console.log("[RESOLUTION_UPDATE] Visible endpoints have no change",forwardedEndpoints,visibleEndpoints)
    }
}

export const sendRecevierVideoConstraints=(endpoints,resolutions)=>{
    const {maxFrameRate}=getPerformanceState();
    const _receiverConstraints={
        lastN: -1,
        selectedSources: [],
        onStageSources: [],
        defaultConstraints: { maxHeight: 0,maxFrameRate: maxFrameRate},
        constraints: {}
    };
    const appMinimized=getAppState() === 'background';
    if(!isElectron || !appMinimized || pictureInPicture.isPIP()){
        const presenterSources=_getSourceNames(endpoints.presenter);
        const mainScreenSources=_getSourceNames(endpoints.mainScreen);
        const stripSources=_getSourceNames(endpoints.strip)
        const spacesViewSources=_getSourceNames(endpoints.spacesView);
        const myStageSources=_getSourceNames(endpoints.myStage);
        const spaceMenuSources=_getSourceNames(endpoints.spaceMenu);
        const spacesManagerSources = _getSourceNames(endpoints.spacesManager);

        console.log("%c[RESOLUTION_UPDATE]%c resolutions",'color:green','color:unset',resolutions,"Endpoints ",endpoints,"main screen ",mainScreenSources)

        spacesViewSources.forEach(source=>{
            if(source.screenshare){
                // A shared screen is native resolution; pinning it to the 180p thumbnail
                // height (resolutions.spacesView) asks the bridge for a layer the sender
                // may not produce, which leaves it suspended under low bandwidth. Request
                // full resolution like the main-grid/presenter paths instead.
                _receiverConstraints.constraints[source.name] = {maxHeight:-1,maxFrameRate:30};
            }else if(!source?.force180p){
                _receiverConstraints.constraints[source.name] = source?.force180p ? resolutions.strip : resolutions.spacesView;
            }
        })
        spaceMenuSources.forEach(source=>{
            if(source.screenshare){
                _receiverConstraints.constraints[source.name] = {maxHeight:-1,maxFrameRate:30};
            }else if(!source?.force180p){
                _receiverConstraints.constraints[source.name] = source?.force180p ? resolutions.strip : resolutions.spaceMenu;
            }
        })

        //less priority first
        mainScreenSources.forEach(source=>{
            _receiverConstraints.constraints[source.name] = source.screenshare ? {maxHeight:-1,maxFrameRate:30} : source?.force180p ? resolutions.strip : resolutions.mainScreen;
        });
        if(mainScreenSources.length){
            _receiverConstraints.selectedSources = [...new Set([...mainScreenSources.map(s=>s.name)])]
        }
        // console.log("%c[RESOLUTION_UPDATE]%c main screen sources resolution",'color:green','color:unset', _receiverConstraints)
        if(!appMinimized){
            stripSources.forEach(source=>{
                _receiverConstraints.constraints[source.name] = resolutions.strip;
            })
        }
        myStageSources.forEach(source=>{
            _receiverConstraints.constraints[source.name] = resolutions.myStage;
        })
        presenterSources.forEach(source=>{
            _receiverConstraints.constraints[source.name] = source.screenshare ? {maxHeight:-1,maxFrameRate:30} : source?.force180p ? resolutions.strip : resolutions.presenter;
        })
        _receiverConstraints.onStageSources=presenterSources.map(s=>s.name);

        //spaces manager sources

        spacesManagerSources.forEach(source=>{
            _receiverConstraints.constraints[source.name] = source.screenshare ? {maxHeight:-1,maxFrameRate:30} : resolutions.spacesManager;
        })

    }else{
        console.log("App is in background disabe videos")
    }
    try {
        console.log("%c[RESOLUTION_UPDATE]%c Sending reciver constraints",'color:green','color:unset',_receiverConstraints,' max resolution',getCurrentMaxResolution())
        room.setReceiverVideoConstraint(getCurrentMaxResolution());
        LAST_RECEIVER_CONSTRAINTS = JSON.parse(JSON.stringify(_receiverConstraints));
        room.setReceiverConstraints(_receiverConstraints);
        setForwardedEndpoints(endpoints);
        setForwardedResolutions(resolutions);
        try {
            room.setSenderVideoConstraint(getCurrentMaxResolution())
        } catch (error) {
            console.error(error)
        }
    } catch (error) {
        console.log("Receiver constraints not sent",error)
    }
}

export const debounce = (func, delay)=>{
    let timeoutId;
    return function(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
}

const debouncedSendReceiverVideoConstraints = debounce(sendRecevierVideoConstraints, 700);

// Milliseconds to keep the screenshare source suspended during a keyframe nudge before restoring full
// receiver constraints. Dropping the source to maxHeight:0 then restoring it forces the bridge to send
// a fresh keyframe to this receiver, which recovers a receiver-only screenshare freeze.
const KEYFRAME_NUDGE_MS = 500;

// Snapshot of the last receiver constraints sent to the bridge, used to rebuild a "suspend one source"
// payload without disturbing the other forwarded sources.
let LAST_RECEIVER_CONSTRAINTS = null;

export const requestScreenshareKeyframe = (sourceName) => {
    try {
        if (!room || !sourceName) { return; }
        const base = LAST_RECEIVER_CONSTRAINTS;
        if (!base || !base.constraints || !(sourceName in base.constraints)) {
            updateVideoQuality(false, true);
            return;
        }
        const suspended = JSON.parse(JSON.stringify(base));
        suspended.constraints[sourceName] = { maxHeight: 0, maxFrameRate: 0 };
        suspended.selectedSources = (suspended.selectedSources || []).filter(s => s !== sourceName);
        suspended.onStageSources = (suspended.onStageSources || []).filter(s => s !== sourceName);
        room.setReceiverConstraints(suspended);
        setTimeout(() => {
            try {
                const endpoints = getScreenVisibleParticipants();
                const resolutions = getResolutionPerScreen(endpoints);
                sendRecevierVideoConstraints(endpoints, resolutions);
            } catch (error) {
                console.error("[Kino] requestScreenshareKeyframe restore failed", error);
            }
        }, KEYFRAME_NUDGE_MS);
    } catch (error) {
        console.error("[Kino] requestScreenshareKeyframe failed", error);
    }
};

